import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Container,
  Paper,
  Typography,
  Avatar,
  Chip,
  Stack,
  IconButton,
  Skeleton,
  Alert,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Tooltip,
  Breadcrumbs,
  Link,
  Collapse,
  TablePagination,
  Grid,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Snackbar,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PersonIcon from "@mui/icons-material/Person";
import HealthAndSafetyIcon from "@mui/icons-material/HealthAndSafety";
import AssignmentIcon from "@mui/icons-material/Assignment";
import CardMembershipIcon from "@mui/icons-material/CardMembership";
import NotesIcon from "@mui/icons-material/Notes";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import HomeIcon from "@mui/icons-material/Home";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import WcIcon from "@mui/icons-material/Wc";
import BadgeIcon from "@mui/icons-material/Badge";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import MedicalServicesIcon from "@mui/icons-material/MedicalServices";
import FingerprintIcon from "@mui/icons-material/Fingerprint";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import SmartphoneIcon from "@mui/icons-material/Smartphone";
import HotelIcon from "@mui/icons-material/Hotel";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import ShieldIcon from "@mui/icons-material/Shield";
import TimelineIcon from "@mui/icons-material/Timeline";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import CircleIcon from "@mui/icons-material/Circle";
import MedicationIcon from "@mui/icons-material/Medication";
import ScienceIcon from "@mui/icons-material/Science";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import HistoryIcon from "@mui/icons-material/History";
import ScheduleIcon from "@mui/icons-material/Schedule";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import CallIcon from "@mui/icons-material/Call";
import SendIcon from "@mui/icons-material/Send";
import NoteAddIcon from "@mui/icons-material/NoteAdd";
import AddTaskIcon from "@mui/icons-material/AddTask";
import FlashOnIcon from "@mui/icons-material/FlashOn";
import CloseIcon from "@mui/icons-material/Close";

// Componentes reutilizables
import ContactDialog from "../components/CareManager/ContactDialog";

// Servicios de datos del paciente
import { getPatientInfo } from "../../../utils/services/Patients/getPatientInfo";
import { getPatientMembership } from "../../../utils/services/Patients/getPatientMembership";
import getPatientCareGaps from "../../../utils/services/Caregaps/getPatientCareGaps";
import getObservationsByPatient from "../../../utils/services/Observations/getObservationsByPatient";
import { useObservationsModal } from "../../../context/ObservationsModalContext/ObservationsModalContext";
import { getCarePlansByPatient } from "../../../utils/services/Careplans/getCarePlansByPatient";
import getCodingOpportunities from "../../../utils/services/CodingOpportunities/getCodingOpportunities";
import { getDecisionColor } from "../../coding-opportunities/utils/codingOpStorage";

// Servicios clinicos
import { getAdmittedPatients } from "../../clinical-info/services/getAdmittedPatients";
import { getDischargedPatients } from "../../clinical-info/services/getDischargedPatients";
import { getEmergencyPatients } from "../../clinical-info/services/getEmergencyPatients";
import { getHighRiskPatients } from "../../clinical-info/services/getHighRiskPatients";
import { getHighCostPatients } from "../../clinical-info/services/getHighCostPatients";

// AdvancedMD services (llamadas directas, sin depender del context)
import { matchAdvancedMdPatientAbility } from "../../../utils/services/AdvancedMD/advancedMdMatchAbility";
import { getPatientLabs } from "../../../utils/services/AdvancedMD/labs";
import { getPatientMedications } from "../../../utils/services/AdvancedMD/medications";

// ---- Helpers ----

const formatDate = (val) => {
  if (!val) return "---";
  try {
    const d = new Date(val);
    if (isNaN(d.getTime())) return "---";
    return d.toISOString().slice(0, 10);
  } catch {
    return "---";
  }
};

const formatDateShort = (val) => {
  if (!val) return null;
  const d = new Date(val);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleDateString();
};

const membershipStatus = (status) =>
  status === 0 || status === "0" || status === "ACTIVE" ? "ACTIVE" : "INACTIVE";

const getInitials = (name) => {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return parts[0][0]?.toUpperCase() || "?";
};

const avatarColor = (gender) => {
  const g = (gender || "").toUpperCase();
  if (g === "M" || g === "MALE") return "#1976d2";
  if (g === "F" || g === "FEMALE") return "#e91e8f";
  return "#757575";
};

// Map gender from API to match payload format
const mapGenderForMatch = (gender) => {
  const g = (gender || "").toUpperCase();
  if (g === "MALE" || g === "M") return "M";
  if (g === "FEMALE" || g === "F") return "F";
  return g;
};

// ---- Sub-components ----

function InfoRow({ icon, label, value }) {
  return (
    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 0.75 }}>
      <Box sx={{ color: "text.secondary", display: "flex", alignItems: "center", minWidth: 24 }}>
        {icon}
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 120, fontWeight: 500 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 400 }}>
        {value || "---"}
      </Typography>
    </Stack>
  );
}

function CardSkeleton({ lines = 4 }) {
  return (
    <Paper variant="outlined" sx={{ p: 3, borderRadius: 3 }}>
      <Skeleton variant="text" width="40%" height={32} sx={{ mb: 2 }} />
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton key={i} variant="text" width={`${70 + Math.random() * 25}%`} height={24} sx={{ mb: 1 }} />
      ))}
    </Paper>
  );
}

function SectionHeader({ icon, title, count, color = "primary" }) {
  return (
    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
      <Box sx={{ color: `${color}.main`, display: "flex" }}>{icon}</Box>
      <Typography variant="h6" fontWeight={600}>{title}</Typography>
      {count !== undefined && count !== null && (
        <Chip label={count} size="small" color={color} sx={{ fontWeight: 700, minWidth: 28, height: 24 }} />
      )}
    </Stack>
  );
}

// ---- Care Gap helpers ----

const gapStatusColor = (status) => {
  const s = (status || "").toLowerCase();
  if (s.includes("open") || s.includes("unresolved")) return "error";
  if (s.includes("closed") || s.includes("resolved")) return "success";
  if (s.includes("pending")) return "warning";
  return "default";
};

// ---- Clinical record table ----

function ClinicalRecordsCard({ title, icon, color, records, loading, error, columns }) {
  if (loading) return <CardSkeleton lines={3} />;

  const hasRecords = records && records.length > 0;

  return (
    <Paper variant="outlined" sx={{ p: 3, borderRadius: 3 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <Box sx={{ color: `${color}.main`, display: "flex" }}>{icon}</Box>
        <Typography variant="h6" fontWeight={600}>{title}</Typography>
        <Chip
          label={records?.length || 0}
          size="small"
          color={hasRecords ? color : "default"}
          sx={{ fontWeight: 700, minWidth: 28, height: 24 }}
        />
      </Stack>
      <Divider sx={{ mb: 2 }} />

      {error ? (
        <Alert severity="warning" sx={{ borderRadius: 2 }}>{error}</Alert>
      ) : !hasRecords ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: "center" }}>
          No records found
        </Typography>
      ) : (
        <TableContainer sx={{ maxHeight: 300 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {columns.map((col) => (
                  <TableCell key={col.key} sx={{ fontWeight: 600, fontSize: "0.75rem" }}>
                    {col.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {records.map((row, idx) => (
                <TableRow key={idx} hover>
                  {columns.map((col) => (
                    <TableCell key={col.key} sx={{ fontSize: "0.8rem" }}>
                      {col.render ? col.render(row) : (row[col.key] || "---")}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );
}

// ---- Collapsible Section Wrapper ----

function CollapsibleSection({ title, icon, color, count, children, defaultOpen = false, loading = false }) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <Paper variant="outlined" sx={{ borderRadius: 3, overflow: "hidden" }}>
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        onClick={() => !loading && setOpen((p) => !p)}
        sx={{
          p: 2.5,
          cursor: loading ? "default" : "pointer",
          "&:hover": loading ? {} : { bgcolor: "action.hover" },
          transition: "background-color 200ms",
        }}
      >
        <Box sx={{ color: `${color}.main`, display: "flex" }}>{icon}</Box>
        <Typography variant="h6" fontWeight={600} sx={{ flex: 1 }}>
          {title}
        </Typography>
        {count !== undefined && (
          <Chip label={count} size="small" color={color} sx={{ fontWeight: 700, minWidth: 28, height: 24 }} />
        )}
        {loading ? (
          <CircularProgress size={20} />
        ) : (
          <IconButton size="small" sx={{ p: 0 }}>
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        )}
      </Stack>
      <Collapse in={open && !loading}>
        <Divider />
        <Box sx={{ p: 2.5, pt: 2 }}>
          {children}
        </Box>
      </Collapse>
    </Paper>
  );
}

// ---- Quick Action Dialogs ----

const NOTE_TYPES = [
  "Progress Note",
  "Phone Note",
  "Assessment Note",
  "Follow-Up Note",
  "Referral Note",
  "Discharge Summary",
];

const OBSERVATION_TYPES = [
  "Vital Signs",
  "Clinical Observation",
  "Behavioral Observation",
  "Social Determinant",
  "Medication Adherence",
  "Patient Reported Outcome",
];

const TASK_PRIORITIES = ["High", "Medium", "Low"];

function QuickActionDialog({ open, onClose, title, icon, children, onSubmit, submitLabel = "Save" }) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" spacing={1.5} alignItems="center">
            {icon}
            <Typography variant="h6" fontWeight={700}>{title}</Typography>
          </Stack>
          <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent sx={{ pt: 2 }}>{children}</DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} variant="outlined" sx={{ textTransform: "none" }}>Cancel</Button>
        <Button onClick={onSubmit} variant="contained" sx={{ textTransform: "none", fontWeight: 600 }}>{submitLabel}</Button>
      </DialogActions>
    </Dialog>
  );
}

function SendEmailDialog({ open, onClose, patientEmail, patientName, onSuccess }) {
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");

  const handleSubmit = () => {
    onSuccess("Email queued successfully");
    setSubject("");
    setBody("");
    onClose();
  };

  return (
    <QuickActionDialog
      open={open}
      onClose={onClose}
      title="Send Email"
      icon={<SendIcon color="secondary" />}
      onSubmit={handleSubmit}
      submitLabel="Send Email"
    >
      <Stack spacing={2.5} sx={{ pt: 1 }}>
        <TextField label="To" value={patientEmail || "No email available"} disabled fullWidth size="small" />
        <TextField label="Subject" value={subject} onChange={(e) => setSubject(e.target.value)} fullWidth size="small" placeholder="Enter email subject..." />
        <TextField
          label="Message"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          fullWidth
          multiline
          rows={5}
          placeholder={`Dear ${patientName},\n\n`}
        />
      </Stack>
    </QuickActionDialog>
  );
}

function ClinicalNoteDialog({ open, onClose, patientName, onSuccess }) {
  const [noteType, setNoteType] = useState("");
  const [noteContent, setNoteContent] = useState("");

  const handleSubmit = () => {
    onSuccess("Clinical note saved successfully");
    setNoteType("");
    setNoteContent("");
    onClose();
  };

  return (
    <QuickActionDialog
      open={open}
      onClose={onClose}
      title="Register Clinical Note"
      icon={<NoteAddIcon color="info" />}
      onSubmit={handleSubmit}
      submitLabel="Save Note"
    >
      <Stack spacing={2.5} sx={{ pt: 1 }}>
        <TextField label="Patient" value={patientName} disabled fullWidth size="small" />
        <TextField
          select
          label="Note Type"
          value={noteType}
          onChange={(e) => setNoteType(e.target.value)}
          fullWidth
          size="small"
        >
          {NOTE_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
        </TextField>
        <TextField
          label="Note Content"
          value={noteContent}
          onChange={(e) => setNoteContent(e.target.value)}
          fullWidth
          multiline
          rows={5}
          placeholder="Enter clinical note details..."
        />
      </Stack>
    </QuickActionDialog>
  );
}

// ObservationDialog removed — replaced by centralized ObservationsModal

function AssignTaskDialog({ open, onClose, patientName, onSuccess }) {
  const [taskDesc, setTaskDesc] = useState("");
  const [assignee, setAssignee] = useState("");
  const [priority, setPriority] = useState("Medium");
  const [dueDate, setDueDate] = useState("");

  const handleSubmit = () => {
    onSuccess("Task assigned successfully");
    setTaskDesc("");
    setAssignee("");
    setPriority("Medium");
    setDueDate("");
    onClose();
  };

  return (
    <QuickActionDialog
      open={open}
      onClose={onClose}
      title="Assign Task"
      icon={<AddTaskIcon color="success" />}
      onSubmit={handleSubmit}
      submitLabel="Assign Task"
    >
      <Stack spacing={2.5} sx={{ pt: 1 }}>
        <TextField label="Patient" value={patientName} disabled fullWidth size="small" />
        <TextField label="Task Description" value={taskDesc} onChange={(e) => setTaskDesc(e.target.value)} fullWidth size="small" placeholder="Describe the task..." />
        <TextField label="Assign To" value={assignee} onChange={(e) => setAssignee(e.target.value)} fullWidth size="small" placeholder="Employee name or role..." />
        <Stack direction="row" spacing={2}>
          <TextField
            select
            label="Priority"
            value={priority}
            onChange={(e) => setPriority(e.target.value)}
            size="small"
            sx={{ minWidth: 140 }}
          >
            {TASK_PRIORITIES.map((p) => <MenuItem key={p} value={p}>{p}</MenuItem>)}
          </TextField>
          <TextField
            label="Due Date"
            type="date"
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
            size="small"
            sx={{ flex: 1 }}
            InputLabelProps={{ shrink: true }}
          />
        </Stack>
      </Stack>
    </QuickActionDialog>
  );
}

// ---- Mock data generators for traceability & tasks ----

const generateMockTraceability = () => {
  const today = new Date();
  const mockRecords = [
    { date: new Date(today - 2 * 86400000).toISOString(), event: "Phone Contact", detail: "Follow-up call by Care Manager - patient confirmed medication adherence", type: "contact" },
    { date: new Date(today - 5 * 86400000).toISOString(), event: "Office Visit", detail: "Routine check-up with PCP Dr. Martinez - vitals recorded", type: "visit" },
    { date: new Date(today - 8 * 86400000).toISOString(), event: "Lab Work Completed", detail: "A1c, lipid panel, CBC drawn at Fort Myers lab", type: "observation" },
    { date: new Date(today - 12 * 86400000).toISOString(), event: "Care Plan Review", detail: "Quarterly care plan review - goals updated by care team", type: "careplan" },
    { date: new Date(today - 18 * 86400000).toISOString(), event: "Phone Contact", detail: "Wellness check call - discussed diet and exercise plan", type: "contact" },
    { date: new Date(today - 25 * 86400000).toISOString(), event: "Specialist Referral", detail: "Referred to Cardiology - Dr. Thompson for follow-up", type: "visit" },
    { date: new Date(today - 30 * 86400000).toISOString(), event: "Medication Change", detail: "Metformin dose adjusted from 500mg to 1000mg BID", type: "observation" },
    { date: new Date(today - 38 * 86400000).toISOString(), event: "Home Visit", detail: "Home health assessment by RN Garcia - fall risk evaluated", type: "visit" },
    { date: new Date(today - 45 * 86400000).toISOString(), event: "Phone Contact", detail: "Transportation assistance arranged for next appointment", type: "contact" },
    { date: new Date(today - 52 * 86400000).toISOString(), event: "Care Plan Created", detail: "Initial comprehensive care plan established", type: "careplan" },
    { date: new Date(today - 60 * 86400000).toISOString(), event: "Office Visit", detail: "Initial assessment - HRA completed, risk stratification done", type: "visit" },
    { date: new Date(today - 75 * 86400000).toISOString(), event: "Enrollment", detail: "Patient enrolled in care management program", type: "contact" },
  ];
  return mockRecords;
};

const generateMockTasks = () => [
  { task: "Complete Annual Wellness Visit scheduling", assignedTo: "Maria Rodriguez, CM", status: "Completed", date: new Date(Date.now() - 3 * 86400000).toISOString() },
  { task: "Follow up on A1c lab results - review with PCP", assignedTo: "Dr. James Martinez, PCP", status: "Completed", date: new Date(Date.now() - 5 * 86400000).toISOString() },
  { task: "Medication reconciliation after hospital discharge", assignedTo: "Sarah Johnson, RN", status: "In Progress", date: new Date(Date.now() - 7 * 86400000).toISOString() },
  { task: "Coordinate cardiology referral appointment", assignedTo: "Maria Rodriguez, CM", status: "In Progress", date: new Date(Date.now() - 10 * 86400000).toISOString() },
  { task: "Submit prior authorization for cardiac imaging", assignedTo: "Linda Chen, Auth Specialist", status: "Pending", date: new Date(Date.now() - 12 * 86400000).toISOString() },
  { task: "Update care plan goals - diabetes management", assignedTo: "Maria Rodriguez, CM", status: "Completed", date: new Date(Date.now() - 15 * 86400000).toISOString() },
  { task: "Arrange home health aide evaluation", assignedTo: "Patricia Williams, SW", status: "Completed", date: new Date(Date.now() - 20 * 86400000).toISOString() },
  { task: "Verify DME order - blood glucose monitor", assignedTo: "Sarah Johnson, RN", status: "Completed", date: new Date(Date.now() - 22 * 86400000).toISOString() },
  { task: "Follow up on missed appointment - reschedule", assignedTo: "Maria Rodriguez, CM", status: "Open", date: new Date(Date.now() - 25 * 86400000).toISOString() },
  { task: "Review polypharmacy risk - consult with pharmacist", assignedTo: "Dr. James Martinez, PCP", status: "Pending", date: new Date(Date.now() - 28 * 86400000).toISOString() },
  { task: "Complete HEDIS gap closure - BCS screening", assignedTo: "Sarah Johnson, RN", status: "Open", date: new Date(Date.now() - 32 * 86400000).toISOString() },
  { task: "Social determinants assessment - food insecurity", assignedTo: "Patricia Williams, SW", status: "Completed", date: new Date(Date.now() - 40 * 86400000).toISOString() },
];

// ================================================================
// PatientInfoPage
// ================================================================

export default function PatientInfoPage() {
  const { cipId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const patientRow = location.state?.patientRow || null;
  const returnPath = location.state?.returnPath || null;

  // --- State: datos del paciente ---
  const [demographics, setDemographics] = useState(null);
  const [memberships, setMemberships] = useState(null);
  const [careGaps, setCareGaps] = useState(null);
  const [observations, setObservations] = useState(null);
  const [carePlans, setCarePlans] = useState(null);
  const [codingOps, setCodingOps] = useState(null);

  // --- State: datos clinicos ---
  const [admitted, setAdmitted] = useState(null);
  const [discharged, setDischarged] = useState(null);
  const [emergency, setEmergency] = useState(null);
  const [highRisk, setHighRisk] = useState(null);
  const [highCost, setHighCost] = useState(null);

  // --- State: AdvancedMD ---
  const [advancedMdId, setAdvancedMdId] = useState(null);
  const [matchStatus, setMatchStatus] = useState("idle"); // idle | loading | matched | no_match | error
  const [medications, setMedications] = useState([]);
  const [labs, setLabs] = useState([]);
  const [loadingMeds, setLoadingMeds] = useState(false);
  const [loadingLabs, setLoadingLabs] = useState(false);
  const [medsError, setMedsError] = useState(null);
  const [labsError, setLabsError] = useState(null);

  // --- Pagination for meds & labs ---
  const [medsPage, setMedsPage] = useState(0);
  const [medsRowsPerPage, setMedsRowsPerPage] = useState(5);
  const [labsPage, setLabsPage] = useState(0);
  const [labsRowsPerPage, setLabsRowsPerPage] = useState(10);

  // --- Loading states ---
  const [loadingDemo, setLoadingDemo] = useState(true);
  const [loadingMemberships, setLoadingMemberships] = useState(true);
  const [loadingGaps, setLoadingGaps] = useState(true);
  const [loadingObs, setLoadingObs] = useState(true);
  const [loadingPlans, setLoadingPlans] = useState(true);
  const [loadingCodingOps, setLoadingCodingOps] = useState(true);
  const [loadingClinical, setLoadingClinical] = useState(true);

  // --- Error states ---
  const [errorDemo, setErrorDemo] = useState(null);
  const [errorMemberships, setErrorMemberships] = useState(null);
  const [errorGaps, setErrorGaps] = useState(null);
  const [errorObs, setErrorObs] = useState(null);
  const [errorPlans, setErrorPlans] = useState(null);
  const [errorCodingOps, setErrorCodingOps] = useState(null);
  const [errorClinical, setErrorClinical] = useState(null);

  const [copiedCip, setCopiedCip] = useState(false);
  const [showAllGaps, setShowAllGaps] = useState(false);

  // --- Quick Actions dialogs ---
  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [noteDialogOpen, setNoteDialogOpen] = useState(false);
  const { showObservationsModal } = useObservationsModal();
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [snackbarMsg, setSnackbarMsg] = useState("");

  // --- Traceability timeline (datos clinicos reales + complementarios) ---
  const traceabilityRecords = useMemo(() => {
    const records = [];

    // Registros reales de admitted
    if (admitted?.length) {
      admitted.forEach((r) => {
        if (r.admitDate) records.push({ date: r.admitDate, event: "Admitted", detail: r.medicalCenter || r.facility || "", type: "admitted" });
      });
    }
    // Registros reales de discharged
    if (discharged?.length) {
      discharged.forEach((r) => {
        if (r.dischargeDate) records.push({ date: r.dischargeDate, event: "Discharged", detail: r.medicalCenter || r.type || "", type: "discharged" });
      });
    }
    // Registros reales de emergency
    if (emergency?.length) {
      emergency.forEach((r) => {
        if (r.admitDate) records.push({ date: r.admitDate, event: "Emergency Visit", detail: r.medicalCenter || "", type: "emergency" });
      });
    }

    // Complementar con eventos de actividad reciente
    if (carePlans?.length) {
      carePlans.forEach((p) => {
        const d = p.createdDate || p.createdAt || p.startDate;
        if (d) records.push({ date: d, event: "Care Plan Created", detail: p.planName || p.name || p.title || "", type: "careplan" });
      });
    }
    if (observations?.length) {
      observations.slice(0, 5).forEach((o) => {
        const d = o.observationDate || o.date || o.createdDate;
        if (d) records.push({ date: d, event: "Observation Recorded", detail: o.observationType || o.type || o.category || "", type: "observation" });
      });
    }

    // Agregar datos complementarios de actividad del equipo
    const mockRecords = generateMockTraceability();
    records.push(...mockRecords);

    // Ordenar por fecha descendente
    records.sort((a, b) => new Date(b.date) - new Date(a.date));
    return records;
  }, [admitted, discharged, emergency, carePlans, observations]);

  // --- Tasks asignadas al paciente ---
  const patientTasks = useMemo(() => {
    const tasks = [];

    // Construir desde care gaps que tengan assignedEmployeeId
    if (careGaps?.length) {
      careGaps.forEach((g) => {
        if (g.assignedEmployeeId || g.assignedTo) {
          tasks.push({
            task: `Follow up: ${g.gapName || g.measureName || g.name || "Care Gap"}`,
            assignedTo: g.assignedEmployeeName || g.assignedTo || `Employee #${g.assignedEmployeeId}`,
            status: g.status || g.gapStatus || "Open",
            date: g.lastUpdated || g.updatedAt || g.createdDate || "",
          });
        }
      });
    }

    // Construir desde observations que tengan empleado
    if (observations?.length) {
      observations.forEach((o) => {
        if (o.createdBy || o.employeeName || o.assignedTo) {
          tasks.push({
            task: `${o.observationType || o.type || "Observation"}: ${o.notes || o.description || o.value || ""}`.slice(0, 80),
            assignedTo: o.createdBy || o.employeeName || o.assignedTo || "---",
            status: o.status || "Completed",
            date: o.observationDate || o.date || o.createdDate || "",
          });
        }
      });
    }

    // Agregar tareas complementarias del equipo de trabajo
    const mockTasks = generateMockTasks();
    tasks.push(...mockTasks);

    // Ordenar por fecha descendente
    tasks.sort((a, b) => new Date(b.date) - new Date(a.date));
    return tasks;
  }, [careGaps, observations]);

  // --- AdvancedMD Match + Load meds/labs ---
  const runAdvancedMdMatch = useCallback(async (demoData) => {
    if (!demoData) return;

    const firstName = demoData.firstName || "";
    const lastName = demoData.lastName || "";
    const dob = demoData.dateOfBirth || "";
    const sex = mapGenderForMatch(demoData.gender);

    if (!firstName && !lastName) {
      setMatchStatus("no_match");
      return;
    }

    setMatchStatus("loading");

    try {
      const data = await matchAdvancedMdPatientAbility({ firstName, lastName, dob, sex });

      // Parsear respuesta (soporta multiples formatos)
      let bestMatch = null;
      if (Array.isArray(data) && data.length > 0) {
        bestMatch = data[0];
      } else if (data?.bestMatch) {
        bestMatch = data.bestMatch;
      } else if (data?.matches?.length > 0) {
        bestMatch = data.matches[0];
      } else if (data?.id || data?.Id) {
        bestMatch = data;
      }

      if (!bestMatch) {
        setMatchStatus("no_match");
        return;
      }

      // Extraer patientId y normalizar (quitar prefijo "pat")
      let pid = bestMatch.id || bestMatch.Id || bestMatch.patientId || "";
      pid = String(pid).replace(/^pat/i, "");

      if (!pid) {
        setMatchStatus("no_match");
        return;
      }

      setAdvancedMdId(pid);
      setMatchStatus("matched");

      // Cargar labs y medicamentos en paralelo
      setLoadingMeds(true);
      setLoadingLabs(true);

      const [medsResult, labsResult] = await Promise.allSettled([
        getPatientMedications(pid),
        getPatientLabs(pid),
      ]);

      if (medsResult.status === "fulfilled") {
        setMedications(Array.isArray(medsResult.value) ? medsResult.value : []);
      } else {
        setMedsError(medsResult.reason?.message || "Error loading medications");
      }
      setLoadingMeds(false);

      if (labsResult.status === "fulfilled") {
        setLabs(Array.isArray(labsResult.value) ? labsResult.value : []);
      } else {
        setLabsError(labsResult.reason?.message || "Error loading labs");
      }
      setLoadingLabs(false);

    } catch (err) {
      console.error("AdvancedMD match error:", err);
      setMatchStatus("error");
    }
  }, []);

  // --- Carga de datos en paralelo ---
  const loadData = useCallback(async () => {
    if (!cipId) return;

    // Grupo 1: datos del paciente
    const patientPromises = [
      getPatientInfo(cipId)
        .then((data) => {
          const record = Array.isArray(data) ? data[0] : data;
          setDemographics(record || null);
          setErrorDemo(null);
          // Disparar match AdvancedMD con los datos demograficos
          if (record) runAdvancedMdMatch(record);
        })
        .catch((err) => setErrorDemo(err.message || "Error loading patient info"))
        .finally(() => setLoadingDemo(false)),

      getPatientMembership(cipId)
        .then((data) => {
          setMemberships(Array.isArray(data) ? data : data ? [data] : []);
          setErrorMemberships(null);
        })
        .catch((err) => setErrorMemberships(err.message || "Error loading memberships"))
        .finally(() => setLoadingMemberships(false)),

      getPatientCareGaps(cipId)
        .then((resp) => {
          const list = resp?.data || (Array.isArray(resp) ? resp : []);
          setCareGaps(list);
          setErrorGaps(null);
        })
        .catch((err) => setErrorGaps(err.message || "Error loading care gaps"))
        .finally(() => setLoadingGaps(false)),

      getObservationsByPatient(cipId)
        .then((data) => {
          setObservations(Array.isArray(data) ? data : data ? [data] : []);
          setErrorObs(null);
        })
        .catch((err) => setErrorObs(err.message || "Error loading observations"))
        .finally(() => setLoadingObs(false)),

      getCarePlansByPatient(cipId)
        .then((data) => {
          setCarePlans(Array.isArray(data) ? data : data ? [data] : []);
          setErrorPlans(null);
        })
        .catch((err) => setErrorPlans(err.message || "Error loading care plans"))
        .finally(() => setLoadingPlans(false)),

      getCodingOpportunities({ cipId, pageSize: 200 })
        .then((data) => {
          setCodingOps(Array.isArray(data?.data) ? data.data : []);
          setErrorCodingOps(null);
        })
        .catch((err) => setErrorCodingOps(err.message || "Error loading coding opportunities"))
        .finally(() => setLoadingCodingOps(false)),
    ];

    // Grupo 2: datos clinicos
    const clinicalPromises = [
      getAdmittedPatients(cipId)
        .then((data) => setAdmitted(Array.isArray(data) ? data : []))
        .catch(() => setAdmitted([])),
      getDischargedPatients(cipId)
        .then((data) => setDischarged(Array.isArray(data) ? data : []))
        .catch(() => setDischarged([])),
      getEmergencyPatients(cipId)
        .then((data) => setEmergency(Array.isArray(data) ? data : []))
        .catch(() => setEmergency([])),
      getHighRiskPatients(cipId)
        .then((data) => setHighRisk(Array.isArray(data) ? data : []))
        .catch(() => setHighRisk([])),
      getHighCostPatients(cipId)
        .then((data) => setHighCost(Array.isArray(data) ? data : []))
        .catch(() => setHighCost([])),
    ];

    await Promise.allSettled([...patientPromises, ...clinicalPromises]);
    setLoadingClinical(false);
  }, [cipId, runAdvancedMdMatch]);

  useEffect(() => { loadData(); }, [loadData]);

  // --- Datos derivados del endpoint real ---
  const demo = demographics || {};
  const patientName =
    demo.memberFullName ||
    [demo.firstName, demo.middleName, demo.lastName].filter(Boolean).join(" ") ||
    patientRow?.name ||
    patientRow?.memberFullName ||
    "Unknown Patient";
  const gender = demo.gender || patientRow?.gender || "";
  const age = demo.age || patientRow?.age || "";
  const dob = demo.dateOfBirth || patientRow?.dob || patientRow?.dateOfBirth || "";
  const mrn = demo.memberId || patientRow?.memberId || "";
  const email = demo.memberEmail || "";
  const phone = demo.memberPhone || "";
  const mobile = demo.memberMobile || "";
  const patientStatus = demo.status || patientRow?.status || "";

  // Flags
  const isAdmitted = (demo.numAdmittedId || 0) > 0 || admitted?.length > 0;
  const isDischarged = (demo.dischargeId || 0) > 0 || discharged?.length > 0;
  const isEmergency = (demo.numEmergencyId || 0) > 0 || emergency?.length > 0;
  const isHighCost = (demo.numHighCostId || 0) > 0 || highCost?.length > 0;
  const isHighRisk = (demo.numHighRiskId || 0) > 0 || highRisk?.length > 0;

  // Meds: separar current vs historic
  const { currentMeds, historicMeds } = useMemo(() => {
    const list = Array.isArray(medications) ? medications : [];
    const currents = list.filter((m) => {
      const status = (m.status || "").toLowerCase();
      return m.currentMedFlag || status.startsWith("current");
    });
    const currentIds = new Set(currents.map((m) => m.id));
    const historic = list.filter((m) => !currentIds.has(m.id));
    return { currentMeds: currents, historicMeds: historic };
  }, [medications]);

  // Labs: agrupar por codigo
  const groupedLabs = useMemo(() => {
    const list = Array.isArray(labs) ? labs : [];
    const groups = {};
    list.forEach((item) => {
      const key = `${item.resultCode || ""}|${item.resultDesc || ""}`;
      if (!groups[key]) {
        groups[key] = {
          resultCode: item.resultCode,
          resultDesc: item.resultDesc,
          values: [],
          units: item.resultUnits || "",
          range: item.resultRange || "",
          status: item.resultStatus || "",
        };
      }
      if (item.resultValue && item.resultValue.trim() !== "") {
        groups[key].values.push(item.resultValue);
      }
      if (!groups[key].units && item.resultUnits) groups[key].units = item.resultUnits;
      if (!groups[key].range && item.resultRange) groups[key].range = item.resultRange;
      if (!groups[key].status && item.resultStatus) groups[key].status = item.resultStatus;
    });
    return Object.values(groups);
  }, [labs]);

  const handleCopyCip = () => {
    navigator.clipboard.writeText(cipId).then(() => {
      setCopiedCip(true);
      setTimeout(() => setCopiedCip(false), 2000);
    });
  };

  const allFailed =
    !loadingDemo && !loadingMemberships && !loadingGaps && !loadingObs && !loadingPlans &&
    errorDemo && errorMemberships && errorGaps && errorObs && errorPlans;

  const MAX_GAPS_DEFAULT = 10;
  const displayedGaps = careGaps && !showAllGaps ? careGaps.slice(0, MAX_GAPS_DEFAULT) : careGaps;

  const activeMemberships = memberships?.filter(
    (m) => membershipStatus(m.status ?? m.numStatus).toUpperCase() === "ACTIVE"
  ).length || 0;

  // --- Columnas para tablas clinicas ---
  const admittedCols = [
    { key: "memberFullName", label: "Patient" },
    { key: "admitDate", label: "Admit Date", render: (r) => formatDate(r.admitDate) },
    { key: "primaryCareProvider", label: "PCP" },
    { key: "medicalCenter", label: "Center" },
    { key: "status", label: "Status", render: (r) => (
      <Chip label={r.status || "---"} size="small" color={r.status === "OPEN" ? "warning" : "default"} sx={{ fontWeight: 600, fontSize: "0.68rem" }} />
    )},
  ];

  const dischargedCols = [
    { key: "memberFullName", label: "Patient" },
    { key: "dischargeDate", label: "Discharge Date", render: (r) => formatDate(r.dischargeDate) },
    { key: "type", label: "Type" },
    { key: "medicalCenter", label: "Center" },
    { key: "status", label: "Status", render: (r) => (
      <Chip label={r.status || "---"} size="small" color={r.status === "OPEN" ? "warning" : "default"} sx={{ fontWeight: 600, fontSize: "0.68rem" }} />
    )},
  ];

  const emergencyCols = [
    { key: "memberFullName", label: "Patient" },
    { key: "admitDate", label: "Admit Date", render: (r) => formatDate(r.admitDate) },
    { key: "primaryCareProvider", label: "PCP" },
    { key: "medicalCenter", label: "Center" },
    { key: "locationAddress", label: "Location" },
    { key: "status", label: "Status", render: (r) => (
      <Chip label={r.status || "---"} size="small" color={r.status === "OPEN" ? "error" : "default"} sx={{ fontWeight: 600, fontSize: "0.68rem" }} />
    )},
  ];

  const highRiskCols = [
    { key: "memberFullName", label: "Patient" },
    { key: "medicalCenter", label: "Center" },
    { key: "primaryCareProvider", label: "PCP" },
    { key: "status", label: "Status", render: (r) => (
      <Chip label={r.status || "---"} size="small" color="warning" sx={{ fontWeight: 600, fontSize: "0.68rem" }} />
    )},
  ];

  const highCostCols = [
    { key: "memberFullName", label: "Patient" },
    { key: "medicalCenter", label: "Center" },
    { key: "primaryCareProvider", label: "PCP" },
    { key: "status", label: "Status", render: (r) => (
      <Chip label={r.status || "---"} size="small" color="error" sx={{ fontWeight: 600, fontSize: "0.68rem" }} />
    )},
  ];

  // Paginated meds
  const allMeds = [...currentMeds, ...historicMeds];
  const paginatedMeds = allMeds.slice(medsPage * medsRowsPerPage, medsPage * medsRowsPerPage + medsRowsPerPage);

  // Paginated labs
  const paginatedLabs = groupedLabs.slice(labsPage * labsRowsPerPage, labsPage * labsRowsPerPage + labsRowsPerPage);

  // ================================================================
  // RENDER
  // ================================================================

  return (
    <Box
      sx={{
        minHeight: "calc(100vh - 64px)",
        py: { xs: 2, md: 3 },
        background: (theme) =>
          `linear-gradient(135deg, ${theme.palette.background.default} 0%, ${theme.palette.action.hover} 50%, ${theme.palette.background.default} 100%)`,
      }}
    >
      <Container maxWidth="xl">
        {/* -- Top Bar -- */}
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1 }}>
          <IconButton onClick={() => returnPath ? navigate(returnPath) : navigate(-1)} size="small">
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h5" fontWeight={700} sx={{ flexGrow: 1 }}>
            Patient Information
          </Typography>
          <Tooltip title={copiedCip ? "Copied!" : "Copy CIP ID"}>
            <Chip
              icon={<ContentCopyIcon sx={{ fontSize: 14 }} />}
              label={`CIP: ${cipId}`}
              onClick={handleCopyCip}
              variant="outlined"
              size="small"
              color={copiedCip ? "success" : "default"}
              sx={{ fontWeight: 600, cursor: "pointer" }}
            />
          </Tooltip>
        </Stack>

        {/* Breadcrumbs */}
        <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ mb: 3 }}>
          <Link
            underline="hover"
            color="inherit"
            sx={{ display: "flex", alignItems: "center", cursor: "pointer" }}
            onClick={() => navigate("/Main")}
          >
            <HomeIcon sx={{ mr: 0.5, fontSize: 18 }} />
            Main
          </Link>
          <Typography color="text.secondary">Patient Info</Typography>
          <Typography color="text.primary" fontWeight={500}>
            {loadingDemo && !patientRow ? "Loading..." : patientName}
          </Typography>
        </Breadcrumbs>

        {/* Error global */}
        {allFailed && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
            Failed to load patient data. Please check your connection and try again.
          </Alert>
        )}

        {/* -- Hero Section -- */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, md: 4 },
            mb: 3,
            borderRadius: 4,
            background: (theme) =>
              `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.06)} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
            border: (theme) => `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
          }}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={3}
            alignItems={{ xs: "center", sm: "flex-start" }}
          >
            {/* Avatar */}
            {loadingDemo && !patientRow ? (
              <Skeleton variant="circular" width={80} height={80} />
            ) : (
              <Avatar
                sx={{
                  width: 80,
                  height: 80,
                  bgcolor: avatarColor(gender),
                  fontSize: 28,
                  fontWeight: 700,
                  boxShadow: (theme) => `0 4px 14px ${alpha(avatarColor(gender), 0.35)}`,
                }}
              >
                {getInitials(patientName)}
              </Avatar>
            )}

            <Box sx={{ flex: 1, textAlign: { xs: "center", sm: "left" } }}>
              {loadingDemo && !patientRow ? (
                <>
                  <Skeleton variant="text" width={240} height={40} />
                  <Skeleton variant="text" width={300} height={28} />
                </>
              ) : (
                <>
                  <Typography variant="h4" fontWeight={700} sx={{ mb: 1 }}>
                    {patientName}
                  </Typography>

                  {/* Info chips */}
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
                    {age && (
                      <Chip icon={<CalendarTodayIcon sx={{ fontSize: 14 }} />} label={`Age: ${age}`} size="small" variant="outlined" />
                    )}
                    {gender && (
                      <Chip icon={<WcIcon sx={{ fontSize: 14 }} />} label={gender} size="small" variant="outlined" />
                    )}
                    {dob && (
                      <Chip label={`DOB: ${formatDate(dob)}`} size="small" variant="outlined" />
                    )}
                    {mrn && (
                      <Chip icon={<BadgeIcon sx={{ fontSize: 14 }} />} label={`MRN: ${mrn}`} size="small" variant="outlined" />
                    )}
                    {patientStatus && (
                      <Chip
                        label={patientStatus}
                        size="small"
                        color={patientStatus === "ACTIVE" ? "success" : "default"}
                        variant="filled"
                        sx={{ fontWeight: 600 }}
                      />
                    )}
                    {/* AdvancedMD match status */}
                    {matchStatus === "matched" && (
                      <Chip
                        icon={<MedicalServicesIcon sx={{ fontSize: 14 }} />}
                        label={`EHR Linked (ID: ${advancedMdId})`}
                        size="small"
                        color="info"
                        variant="outlined"
                        sx={{ fontWeight: 500 }}
                      />
                    )}
                  </Stack>

                  {/* Status flags */}
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                    {isHighRisk && (
                      <Chip label="High Risk" color="warning" size="small" variant="filled" icon={<WarningAmberIcon />} sx={{ fontWeight: 600, fontSize: "0.7rem" }} />
                    )}
                    {isHighCost && (
                      <Chip label="High Cost" color="error" size="small" variant="filled" icon={<MonetizationOnIcon />} sx={{ fontWeight: 600, fontSize: "0.7rem" }} />
                    )}
                    {isEmergency && (
                      <Chip label="Emergency" color="error" size="small" variant="filled" icon={<ReportProblemIcon />} sx={{ fontWeight: 600, fontSize: "0.7rem" }} />
                    )}
                    {isAdmitted && (
                      <Chip label="Admitted" color="info" size="small" variant="filled" icon={<HotelIcon />} sx={{ fontWeight: 600, fontSize: "0.7rem" }} />
                    )}
                    {isDischarged && (
                      <Chip label="Discharged" size="small" variant="filled" icon={<ExitToAppIcon />} sx={{ fontWeight: 600, fontSize: "0.7rem" }} />
                    )}
                  </Stack>
                </>
              )}
            </Box>
          </Stack>
        </Paper>

        {/* -- Quick Actions Bar -- */}
        <Paper
          elevation={0}
          sx={{
            p: 2,
            mb: 3,
            borderRadius: 3,
            border: (theme) => `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
            background: (theme) =>
              `linear-gradient(90deg, ${alpha(theme.palette.info.main, 0.04)} 0%, ${alpha(theme.palette.primary.main, 0.04)} 100%)`,
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            spacing={1.5}
            sx={{ flexWrap: "wrap", rowGap: 1 }}
          >
            <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mr: 1 }}>
              <FlashOnIcon sx={{ fontSize: 20, color: "info.main" }} />
              <Typography variant="subtitle2" fontWeight={700} color="info.main">
                Quick Actions
              </Typography>
            </Stack>

            <Button
              variant="outlined"
              size="small"
              startIcon={<CallIcon />}
              onClick={() => setContactDialogOpen(true)}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                borderRadius: 2,
                borderColor: "success.main",
                color: "success.dark",
                "&:hover": { bgcolor: (t) => alpha(t.palette.success.main, 0.08), borderColor: "success.main" },
              }}
            >
              Register Call
            </Button>

            <Button
              variant="outlined"
              size="small"
              startIcon={<SendIcon />}
              onClick={() => setEmailDialogOpen(true)}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                borderRadius: 2,
                borderColor: "secondary.main",
                color: "secondary.dark",
                "&:hover": { bgcolor: (t) => alpha(t.palette.secondary.main, 0.08), borderColor: "secondary.main" },
              }}
            >
              Send Email
            </Button>

            <Button
              variant="outlined"
              size="small"
              startIcon={<NoteAddIcon />}
              onClick={() => setNoteDialogOpen(true)}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                borderRadius: 2,
                borderColor: "info.main",
                color: "info.dark",
                "&:hover": { bgcolor: (t) => alpha(t.palette.info.main, 0.08), borderColor: "info.main" },
              }}
            >
              Clinical Note
            </Button>

            <Button
              variant="outlined"
              size="small"
              startIcon={<NotesIcon />}
              onClick={() => showObservationsModal(cipId, 1, patientName)}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                borderRadius: 2,
                borderColor: "warning.main",
                color: "warning.dark",
                "&:hover": { bgcolor: (t) => alpha(t.palette.warning.main, 0.08), borderColor: "warning.main" },
              }}
            >
              Observation
            </Button>

            <Button
              variant="outlined"
              size="small"
              startIcon={<AddTaskIcon />}
              onClick={() => setTaskDialogOpen(true)}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                borderRadius: 2,
                borderColor: "primary.main",
                color: "primary.dark",
                "&:hover": { bgcolor: (t) => alpha(t.palette.primary.main, 0.08), borderColor: "primary.main" },
              }}
            >
              Assign Task
            </Button>
          </Stack>
        </Paper>

        {/* -- Main Content Grid -- */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", lg: "2fr 1fr" },
            gap: 3,
            alignItems: "start",
          }}
        >
          {/* === Left Column === */}
          <Stack spacing={3}>
            {/* -- Demographics Card -- */}
            {loadingDemo && !patientRow ? (
              <CardSkeleton lines={8} />
            ) : (
              <Paper variant="outlined" sx={{ p: 3, borderRadius: 3 }}>
                <SectionHeader icon={<PersonIcon />} title="Demographics" />
                <Divider sx={{ mb: 2 }} />
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 0.5 }}>
                  <InfoRow icon={<PersonIcon fontSize="small" />} label="Full Name" value={patientName} />
                  <InfoRow icon={<FingerprintIcon fontSize="small" />} label="CIP ID" value={demo.varCipId || cipId} />
                  <InfoRow icon={<BadgeIcon fontSize="small" />} label="Member ID" value={mrn} />
                  <InfoRow icon={<CalendarTodayIcon fontSize="small" />} label="Date of Birth" value={formatDate(dob)} />
                  <InfoRow icon={<WcIcon fontSize="small" />} label="Gender" value={gender || "---"} />
                  <InfoRow icon={<BadgeIcon fontSize="small" />} label="Age" value={age || "---"} />
                  <InfoRow icon={<EmailIcon fontSize="small" />} label="Email" value={email || "---"} />
                  <InfoRow icon={<PhoneIcon fontSize="small" />} label="Phone" value={phone || "---"} />
                  <InfoRow icon={<SmartphoneIcon fontSize="small" />} label="Mobile" value={mobile || "---"} />
                  <InfoRow icon={<ShieldIcon fontSize="small" />} label="Status" value={patientStatus || "---"} />
                </Box>
                {errorDemo && !patientRow && (
                  <Alert severity="warning" sx={{ mt: 2, borderRadius: 2 }}>{errorDemo}</Alert>
                )}
              </Paper>
            )}

            {/* -- Medications Card (Collapsible + Paginated) -- */}
            <CollapsibleSection
              title="Medications"
              icon={<MedicationIcon />}
              color="info"
              count={medications.length}
              loading={loadingMeds}
              defaultOpen={false}
            >
              {medsError ? (
                <Alert severity="warning" sx={{ borderRadius: 2 }}>{medsError}</Alert>
              ) : medications.length === 0 ? (
                matchStatus === "no_match" || matchStatus === "error" ? (
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: "center" }}>
                    EHR data not available - patient could not be matched in AdvancedMD
                  </Typography>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: "center" }}>
                    No medications found
                  </Typography>
                )
              ) : (
                <>
                  {/* Resumen rapido */}
                  <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                    <Chip
                      icon={<CheckCircleIcon sx={{ fontSize: 14 }} />}
                      label={`${currentMeds.length} Current`}
                      size="small"
                      color="success"
                      variant="outlined"
                      sx={{ fontWeight: 600 }}
                    />
                    <Chip
                      icon={<HistoryIcon sx={{ fontSize: 14 }} />}
                      label={`${historicMeds.length} Historic`}
                      size="small"
                      variant="outlined"
                      sx={{ fontWeight: 600 }}
                    />
                  </Stack>

                  {/* Grid de medicamentos paginado */}
                  <Grid container spacing={1.5}>
                    {paginatedMeds.map((med, idx) => {
                      const isCurrent = med.currentMedFlag || (med.status || "").toLowerCase().startsWith("current");
                      const start = formatDateShort(med.startDate);
                      const stop = formatDateShort(med.stopDate);

                      return (
                        <Grid item xs={12} md={6} key={med.id || idx}>
                          <Paper
                            variant="outlined"
                            sx={{
                              p: 1.5,
                              borderRadius: 2,
                              borderLeft: `3px solid`,
                              borderLeftColor: isCurrent ? "success.main" : "grey.300",
                              opacity: isCurrent ? 1 : 0.85,
                              height: "100%",
                            }}
                          >
                            <Typography variant="subtitle2" fontWeight={600}>
                              {med.drugName}{" "}
                              {med.drugStrength ? `- ${med.drugStrength}` : ""}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                              {med.doseForm}
                              {med.route ? ` - ${med.route}` : ""}
                            </Typography>

                            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mb: 0.5 }}>
                              {med.frequency && (
                                <Chip icon={<ScheduleIcon sx={{ fontSize: 12 }} />} size="small" label={med.frequency} variant="outlined" sx={{ fontSize: "0.65rem", height: 22 }} />
                              )}
                              {med.quantity != null && (
                                <Chip size="small" label={`Qty ${med.quantity}`} variant="outlined" sx={{ fontSize: "0.65rem", height: 22 }} />
                              )}
                              {typeof med.refills === "number" && (
                                <Chip size="small" label={`${med.refills} refills`} variant="outlined" sx={{ fontSize: "0.65rem", height: 22 }} />
                              )}
                              {med.status && (
                                <Chip size="small" label={med.status} color={isCurrent ? "success" : "default"} variant="outlined" sx={{ fontSize: "0.65rem", height: 22 }} />
                              )}
                            </Stack>

                            {med.doctor && (
                              <Typography variant="caption" color="text.secondary">
                                Prescriber: {med.doctor}
                              </Typography>
                            )}
                            {(start || stop) && (
                              <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                                {start && `Start: ${start}`}
                                {start && stop && " - "}
                                {stop && `Stop: ${stop}`}
                              </Typography>
                            )}
                          </Paper>
                        </Grid>
                      );
                    })}
                  </Grid>

                  {allMeds.length > 5 && (
                    <TablePagination
                      component="div"
                      count={allMeds.length}
                      page={medsPage}
                      onPageChange={(_, p) => setMedsPage(p)}
                      rowsPerPage={medsRowsPerPage}
                      onRowsPerPageChange={(e) => { setMedsRowsPerPage(parseInt(e.target.value, 10)); setMedsPage(0); }}
                      rowsPerPageOptions={[5, 10, 25]}
                      labelRowsPerPage="Per page:"
                      sx={{ mt: 1 }}
                    />
                  )}
                </>
              )}
            </CollapsibleSection>

            {/* -- Labs Card (Collapsible + Paginated) -- */}
            <CollapsibleSection
              title="Laboratory Results"
              icon={<ScienceIcon />}
              color="secondary"
              count={groupedLabs.length}
              loading={loadingLabs}
              defaultOpen={false}
            >
              {labsError ? (
                <Alert severity="warning" sx={{ borderRadius: 2 }}>{labsError}</Alert>
              ) : groupedLabs.length === 0 ? (
                matchStatus === "no_match" || matchStatus === "error" ? (
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: "center" }}>
                    EHR data not available - patient could not be matched in AdvancedMD
                  </Typography>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: "center" }}>
                    No laboratory results found
                  </Typography>
                )
              ) : (
                <>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600, fontSize: "0.75rem" }}>Code</TableCell>
                          <TableCell sx={{ fontWeight: 600, fontSize: "0.75rem" }}>Description</TableCell>
                          <TableCell sx={{ fontWeight: 600, fontSize: "0.75rem" }}>Value(s)</TableCell>
                          <TableCell sx={{ fontWeight: 600, fontSize: "0.75rem" }}>Units</TableCell>
                          <TableCell sx={{ fontWeight: 600, fontSize: "0.75rem" }}>Range</TableCell>
                          <TableCell sx={{ fontWeight: 600, fontSize: "0.75rem" }}>Status</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {paginatedLabs.map((lab, idx) => (
                          <TableRow key={idx} hover>
                            <TableCell sx={{ fontSize: "0.8rem", fontFamily: "monospace" }}>{lab.resultCode || "---"}</TableCell>
                            <TableCell sx={{ fontSize: "0.8rem" }}>{lab.resultDesc || "---"}</TableCell>
                            <TableCell sx={{ fontSize: "0.8rem", fontWeight: 600 }}>
                              {lab.values.length > 0 ? lab.values.join(", ") : "---"}
                            </TableCell>
                            <TableCell sx={{ fontSize: "0.8rem" }}>{lab.units || "---"}</TableCell>
                            <TableCell sx={{ fontSize: "0.8rem" }}>{lab.range || "---"}</TableCell>
                            <TableCell>
                              {lab.status ? (
                                <Chip
                                  label={lab.status}
                                  size="small"
                                  color={
                                    lab.status.toLowerCase() === "normal" ? "success"
                                    : lab.status.toLowerCase() === "abnormal" || lab.status.toLowerCase() === "high" || lab.status.toLowerCase() === "low" ? "warning"
                                    : "default"
                                  }
                                  variant="outlined"
                                  sx={{ fontWeight: 600, fontSize: "0.65rem" }}
                                />
                              ) : "---"}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {groupedLabs.length > 10 && (
                    <TablePagination
                      component="div"
                      count={groupedLabs.length}
                      page={labsPage}
                      onPageChange={(_, p) => setLabsPage(p)}
                      rowsPerPage={labsRowsPerPage}
                      onRowsPerPageChange={(e) => { setLabsRowsPerPage(parseInt(e.target.value, 10)); setLabsPage(0); }}
                      rowsPerPageOptions={[10, 25, 50]}
                      labelRowsPerPage="Per page:"
                      sx={{ mt: 1 }}
                    />
                  )}
                </>
              )}
            </CollapsibleSection>

            {/* -- Clinical Data: Admitted -- */}
            <ClinicalRecordsCard
              title="Admitted"
              icon={<HotelIcon />}
              color="info"
              records={admitted}
              loading={loadingClinical}
              error={null}
              columns={admittedCols}
            />

            {/* -- Clinical Data: Discharged -- */}
            <ClinicalRecordsCard
              title="Discharged"
              icon={<ExitToAppIcon />}
              color="primary"
              records={discharged}
              loading={loadingClinical}
              error={null}
              columns={dischargedCols}
            />

            {/* -- Clinical Data: Emergency -- */}
            <ClinicalRecordsCard
              title="Emergency"
              icon={<ReportProblemIcon />}
              color="error"
              records={emergency}
              loading={loadingClinical}
              error={null}
              columns={emergencyCols}
            />

            {/* -- Clinical Data: High Risk -- */}
            <ClinicalRecordsCard
              title="High Risk"
              icon={<WarningAmberIcon />}
              color="warning"
              records={highRisk}
              loading={loadingClinical}
              error={null}
              columns={highRiskCols}
            />

            {/* -- Clinical Data: High Cost -- */}
            <ClinicalRecordsCard
              title="High Cost"
              icon={<MonetizationOnIcon />}
              color="error"
              records={highCost}
              loading={loadingClinical}
              error={null}
              columns={highCostCols}
            />

            {/* -- Care Gaps Card -- */}
            {loadingGaps ? (
              <CardSkeleton lines={6} />
            ) : (
              <Paper variant="outlined" sx={{ p: 3, borderRadius: 3 }}>
                <SectionHeader
                  icon={<HealthAndSafetyIcon />}
                  title="Care Gaps"
                  count={careGaps?.length || 0}
                  color={careGaps?.length > 0 ? "error" : "primary"}
                />
                <Divider sx={{ mb: 2 }} />

                {errorGaps ? (
                  <Alert severity="warning" sx={{ borderRadius: 2 }}>{errorGaps}</Alert>
                ) : !careGaps?.length ? (
                  <Stack alignItems="center" spacing={1} sx={{ py: 4 }}>
                    <CheckCircleOutlineIcon sx={{ fontSize: 40, color: "success.main" }} />
                    <Typography color="text.secondary">No care gaps found</Typography>
                  </Stack>
                ) : (
                  <>
                    <TableContainer sx={{ maxHeight: 400 }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 600, fontSize: "0.75rem" }}>Gap Name</TableCell>
                            <TableCell sx={{ fontWeight: 600, fontSize: "0.75rem" }}>Source Status</TableCell>
                            <TableCell sx={{ fontWeight: 600, fontSize: "0.75rem" }}>Workflow Status</TableCell>
                            <TableCell sx={{ fontWeight: 600, fontSize: "0.75rem" }}>Submitted</TableCell>
                            <TableCell sx={{ fontWeight: 600, fontSize: "0.75rem" }}>Closure Date</TableCell>
                            <TableCell sx={{ fontWeight: 600, fontSize: "0.75rem" }} align="center">Evidence</TableCell>
                            <TableCell sx={{ fontWeight: 600, fontSize: "0.75rem" }} align="center">Outreach</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {displayedGaps.map((gap, idx) => (
                            <TableRow key={gap.careGapId || idx} hover>
                              <TableCell sx={{ fontSize: "0.8rem" }}>{gap.careGapName || "---"}</TableCell>
                              <TableCell>
                                <Chip
                                  label={gap.sourceStatus || gap.sourceStatusCode || "Unknown"}
                                  size="small"
                                  color={gapStatusColor(gap.sourceStatus || gap.sourceStatusCode)}
                                  variant="filled"
                                  sx={{ fontWeight: 600, fontSize: "0.68rem" }}
                                />
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={gap.workflowStatus || gap.workflowStatusCode || "---"}
                                  size="small"
                                  variant="outlined"
                                  sx={{ fontWeight: 600, fontSize: "0.68rem" }}
                                />
                              </TableCell>
                              <TableCell sx={{ fontSize: "0.8rem" }}>{formatDate(gap.submittedDate)}</TableCell>
                              <TableCell sx={{ fontSize: "0.8rem" }}>{formatDate(gap.serviceDateOfClosure)}</TableCell>
                              <TableCell align="center" sx={{ fontSize: "0.8rem" }}>{gap.evidenceCount ?? 0}</TableCell>
                              <TableCell align="center" sx={{ fontSize: "0.8rem" }}>{gap.outreachCount ?? 0}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>

                    {careGaps.length > MAX_GAPS_DEFAULT && (
                      <Box sx={{ textAlign: "center", mt: 1.5 }}>
                        <Button
                          size="small"
                          onClick={() => setShowAllGaps((prev) => !prev)}
                          endIcon={showAllGaps ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        >
                          {showAllGaps ? "Show less" : `Show all ${careGaps.length} gaps`}
                        </Button>
                      </Box>
                    )}
                  </>
                )}
              </Paper>
            )}

            {/* -- Coding Opportunities Card -- */}
            {loadingCodingOps ? (
              <CardSkeleton lines={6} />
            ) : (
              <Paper variant="outlined" sx={{ p: 3, borderRadius: 3 }}>
                <SectionHeader
                  icon={<LocalHospitalIcon />}
                  title="Coding Opportunities"
                  count={codingOps?.length || 0}
                  color={codingOps?.length > 0 ? "secondary" : "primary"}
                />
                <Divider sx={{ mb: 2 }} />

                {errorCodingOps ? (
                  <Alert severity="warning" sx={{ borderRadius: 2 }}>{errorCodingOps}</Alert>
                ) : !codingOps?.length ? (
                  <Stack alignItems="center" spacing={1} sx={{ py: 4 }}>
                    <LocalHospitalIcon sx={{ fontSize: 40, color: "text.disabled" }} />
                    <Typography color="text.secondary">No coding opportunities found</Typography>
                  </Stack>
                ) : (
                  <TableContainer sx={{ maxHeight: 400 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600, fontSize: "0.75rem" }}>HCC</TableCell>
                          <TableCell sx={{ fontWeight: 600, fontSize: "0.75rem" }}>Diagnosis</TableCell>
                          <TableCell sx={{ fontWeight: 600, fontSize: "0.75rem" }}>Year</TableCell>
                          <TableCell sx={{ fontWeight: 600, fontSize: "0.75rem" }}>Status</TableCell>
                          <TableCell sx={{ fontWeight: 600, fontSize: "0.75rem" }}>Last DOS</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {codingOps.map((op) => (
                          <TableRow key={op.codingOpportunityId} hover>
                            <TableCell>
                              <Typography variant="body2" fontWeight={700} sx={{ fontSize: "0.8rem" }}>
                                {op.hccId || "-"}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {op.hccDescription || ""}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ fontSize: "0.8rem" }}>
                              {op.diagnosisCode || "-"}{op.diagnosisDescription ? ` - ${op.diagnosisDescription}` : ""}
                            </TableCell>
                            <TableCell sx={{ fontSize: "0.8rem" }}>{op.measureYear ?? "-"}</TableCell>
                            <TableCell>
                              <Chip
                                label={op.statusName || op.status || "-"}
                                size="small"
                                color={getDecisionColor(op.statusName || op.status)}
                                sx={{ fontWeight: 600, fontSize: "0.68rem" }}
                              />
                            </TableCell>
                            <TableCell sx={{ fontSize: "0.8rem" }}>{op.lastDos ? formatDate(op.lastDos) : "-"}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Paper>
            )}

            {/* -- Care Plans Card -- */}
            {loadingPlans ? (
              <CardSkeleton lines={4} />
            ) : (
              <Paper variant="outlined" sx={{ p: 3, borderRadius: 3 }}>
                <SectionHeader icon={<AssignmentIcon />} title="Care Plans" count={carePlans?.length || 0} />
                <Divider sx={{ mb: 2 }} />

                {errorPlans ? (
                  <Alert severity="warning" sx={{ borderRadius: 2 }}>{errorPlans}</Alert>
                ) : !carePlans?.length ? (
                  <Stack alignItems="center" spacing={1} sx={{ py: 4 }}>
                    <AssignmentIcon sx={{ fontSize: 40, color: "text.disabled" }} />
                    <Typography color="text.secondary">No care plans found</Typography>
                  </Stack>
                ) : (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>Plan Name</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Created</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Last Updated</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {carePlans.map((plan, idx) => (
                          <TableRow key={plan.carePlanId || plan.id || idx} hover>
                            <TableCell>{plan.planName || plan.name || plan.title || plan.description || "---"}</TableCell>
                            <TableCell>
                              <Chip
                                label={plan.status || "Active"}
                                size="small"
                                color={(plan.status || "").toLowerCase() === "active" ? "success" : "default"}
                                variant="outlined"
                                sx={{ fontWeight: 600, fontSize: "0.7rem" }}
                              />
                            </TableCell>
                            <TableCell>{formatDate(plan.createdDate || plan.createdAt || plan.startDate)}</TableCell>
                            <TableCell>{formatDate(plan.updatedDate || plan.updatedAt || plan.modifiedDate)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Paper>
            )}
          </Stack>

          {/* === Right Column === */}
          <Stack spacing={3}>
            {/* -- Memberships Card -- */}
            {loadingMemberships ? (
              <CardSkeleton lines={4} />
            ) : (
              <Paper variant="outlined" sx={{ p: 3, borderRadius: 3 }}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                  <Box sx={{ color: "primary.main", display: "flex" }}><CardMembershipIcon /></Box>
                  <Typography variant="h6" fontWeight={600}>Memberships</Typography>
                  {memberships && (
                    <Chip
                      label={`${activeMemberships} / ${memberships.length}`}
                      size="small"
                      color="primary"
                      variant="outlined"
                      sx={{ fontWeight: 700, height: 24 }}
                    />
                  )}
                </Stack>
                <Divider sx={{ mb: 2 }} />

                {errorMemberships ? (
                  <Alert severity="warning" sx={{ borderRadius: 2 }}>{errorMemberships}</Alert>
                ) : !memberships?.length ? (
                  <Stack alignItems="center" spacing={1} sx={{ py: 4 }}>
                    <CardMembershipIcon sx={{ fontSize: 40, color: "text.disabled" }} />
                    <Typography color="text.secondary">No memberships found</Typography>
                  </Stack>
                ) : (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>Plan</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Network</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Effective</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {memberships.map((m, idx) => {
                          const mStatus = membershipStatus(m.status ?? m.numStatus);
                          return (
                            <TableRow key={m.membershipId || m.id || idx} hover>
                              <TableCell sx={{ fontSize: "0.8rem" }}>
                                {m.varMemberPlan || m.memberPlan || m.planName || "---"}
                              </TableCell>
                              <TableCell sx={{ fontSize: "0.8rem" }}>
                                {m.varNetworkName || m.networkName || m.network || "---"}
                              </TableCell>
                              <TableCell sx={{ fontSize: "0.8rem" }}>
                                {formatDate(m.datMemberPlanEffectiveDate || m.memberPlanEffectiveDate || m.effectiveDate)}
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={mStatus}
                                  size="small"
                                  color={mStatus === "ACTIVE" ? "success" : "default"}
                                  variant="filled"
                                  sx={{ fontWeight: 600, fontSize: "0.65rem" }}
                                />
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Paper>
            )}

            {/* -- Observations Card -- */}
            {loadingObs ? (
              <CardSkeleton lines={5} />
            ) : (
              <Paper variant="outlined" sx={{ p: 3, borderRadius: 3 }}>
                <SectionHeader icon={<NotesIcon />} title="Observations" count={observations?.length || 0} />
                <Divider sx={{ mb: 2 }} />

                {errorObs ? (
                  <Alert severity="warning" sx={{ borderRadius: 2 }}>{errorObs}</Alert>
                ) : !observations?.length ? (
                  <Stack alignItems="center" spacing={1} sx={{ py: 4 }}>
                    <NotesIcon sx={{ fontSize: 40, color: "text.disabled" }} />
                    <Typography color="text.secondary">No observations found</Typography>
                  </Stack>
                ) : (
                  <Stack spacing={1.5}>
                    {observations.map((obs, idx) => (
                      <Paper
                        key={obs.observationId || obs.id || idx}
                        variant="outlined"
                        sx={{
                          p: 2,
                          borderRadius: 2,
                          "&:hover": { bgcolor: (theme) => alpha(theme.palette.primary.main, 0.03) },
                        }}
                      >
                        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body2" fontWeight={600}>
                              {obs.observationType || obs.type || obs.category || obs.name || "Observation"}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {formatDate(obs.observationDate || obs.date || obs.createdDate)}
                            </Typography>
                          </Box>
                          {obs.value && (
                            <Chip label={obs.value} size="small" variant="outlined" sx={{ fontWeight: 500 }} />
                          )}
                        </Stack>
                        {(obs.notes || obs.description || obs.comment) && (
                          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
                            {obs.notes || obs.description || obs.comment}
                          </Typography>
                        )}
                      </Paper>
                    ))}
                  </Stack>
                )}
              </Paper>
            )}

            {/* -- Traceability Timeline Card -- */}
            <Paper variant="outlined" sx={{ p: 3, borderRadius: 3 }}>
              <SectionHeader icon={<TimelineIcon />} title="Traceability" count={traceabilityRecords.length} color="secondary" />
              <Divider sx={{ mb: 2 }} />

              {traceabilityRecords.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: "center" }}>
                  No traceability records found
                </Typography>
              ) : (
                <Stack spacing={0}>
                  {traceabilityRecords.map((rec, idx) => {
                    const typeColors = {
                      admitted: "#1976d2",
                      discharged: "#757575",
                      emergency: "#d32f2f",
                      careplan: "#2e7d32",
                      observation: "#ed6c02",
                      contact: "#0288d1",
                      visit: "#7b1fa2",
                    };
                    const dotColor = typeColors[rec.type] || "#9e9e9e";

                    return (
                      <Stack key={idx} direction="row" spacing={2} sx={{ position: "relative" }}>
                        {/* Linea vertical */}
                        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", minWidth: 24 }}>
                          <CircleIcon sx={{ fontSize: 10, color: dotColor, zIndex: 1 }} />
                          {idx < traceabilityRecords.length - 1 && (
                            <Box sx={{ width: 2, flex: 1, bgcolor: "divider" }} />
                          )}
                        </Box>

                        {/* Contenido */}
                        <Box sx={{ pb: 2, flex: 1 }}>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <Typography variant="body2" fontWeight={600}>
                              {rec.event}
                            </Typography>
                            <Chip
                              label={rec.type.replace(/_/g, " ")}
                              size="small"
                              sx={{
                                height: 18,
                                fontSize: "0.6rem",
                                fontWeight: 600,
                                bgcolor: alpha(dotColor, 0.12),
                                color: dotColor,
                              }}
                            />
                          </Stack>
                          {rec.detail && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                              {rec.detail}
                            </Typography>
                          )}
                          <Typography variant="caption" color="text.disabled">
                            {formatDate(rec.date)}
                          </Typography>
                        </Box>
                      </Stack>
                    );
                  })}
                </Stack>
              )}
            </Paper>

            {/* -- Assigned Tasks Card -- */}
            <Paper variant="outlined" sx={{ p: 3, borderRadius: 3 }}>
              <SectionHeader icon={<TaskAltIcon />} title="Assigned Tasks" count={patientTasks.length} color="info" />
              <Divider sx={{ mb: 2 }} />

              {patientTasks.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: "center" }}>
                  No tasks assigned to this patient
                </Typography>
              ) : (
                <TableContainer sx={{ maxHeight: 500 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600, fontSize: "0.75rem" }}>Task</TableCell>
                        <TableCell sx={{ fontWeight: 600, fontSize: "0.75rem" }}>Assigned To</TableCell>
                        <TableCell sx={{ fontWeight: 600, fontSize: "0.75rem" }}>Status</TableCell>
                        <TableCell sx={{ fontWeight: 600, fontSize: "0.75rem" }}>Date</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {patientTasks.map((t, idx) => {
                        const statusLower = (t.status || "").toLowerCase();
                        const statusColor = statusLower.includes("complet") ? "success"
                          : statusLower.includes("open") || statusLower.includes("pending") ? "warning"
                          : statusLower.includes("overdue") || statusLower.includes("in progress") ? "info"
                          : "default";

                        return (
                          <TableRow key={idx} hover>
                            <TableCell sx={{ fontSize: "0.8rem", maxWidth: 220 }}>
                              <Typography variant="body2" noWrap title={t.task} sx={{ fontSize: "0.8rem" }}>
                                {t.task}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ fontSize: "0.8rem" }}>
                              <Typography variant="body2" fontWeight={500} sx={{ fontSize: "0.8rem" }}>
                                {t.assignedTo}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={t.status}
                                size="small"
                                color={statusColor}
                                variant="filled"
                                sx={{ fontWeight: 600, fontSize: "0.65rem" }}
                              />
                            </TableCell>
                            <TableCell sx={{ fontSize: "0.8rem" }}>
                              {formatDate(t.date)}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>
          </Stack>
        </Box>
      </Container>

      {/* -- Quick Action Dialogs -- */}
      <ContactDialog
        open={contactDialogOpen}
        onClose={() => setContactDialogOpen(false)}
        patient={{
          name: patientName,
          cipId,
          phone,
          mobile,
          email,
        }}
      />

      <SendEmailDialog
        open={emailDialogOpen}
        onClose={() => setEmailDialogOpen(false)}
        patientEmail={email}
        patientName={patientName}
        onSuccess={setSnackbarMsg}
      />

      <ClinicalNoteDialog
        open={noteDialogOpen}
        onClose={() => setNoteDialogOpen(false)}
        patientName={patientName}
        onSuccess={setSnackbarMsg}
      />

      <AssignTaskDialog
        open={taskDialogOpen}
        onClose={() => setTaskDialogOpen(false)}
        patientName={patientName}
        onSuccess={setSnackbarMsg}
      />

      {/* Snackbar de confirmacion */}
      <Snackbar
        open={!!snackbarMsg}
        autoHideDuration={3000}
        onClose={() => setSnackbarMsg("")}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbarMsg("")}
          severity="success"
          variant="filled"
          sx={{ borderRadius: 2, fontWeight: 600 }}
        >
          {snackbarMsg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
